const SERVER = import.meta.env.VITE_API_URL;
import axios from "../configs/axios.config";

const authHeaders = () => {
  const token = localStorage.getItem("prima_session");
  if (!token) {
    throw new Error("Token not found");
  }
  return { Authorization: `Bearer ${token}` };
};

const getVerificationReviews = async ({
  entityType,
  entityIds = [],
  status = "",
  page = 1,
  limit = 100,
  search = "",
} = {}) => {
  try {
    const params = new URLSearchParams({
      entityType,
      page,
      limit,
    });

    if (status) params.append("status", status);
    if (search) params.append("search", search);
    if (entityIds.length) params.append("entityIds", entityIds.join(","));

    const response = await axios.get(
      `${SERVER}/admin/verification-reviews?${params.toString()}`,
      {
        headers: authHeaders(),
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error fetching verification reviews:",
      error.response?.data?.message || error.message,
    );
    throw new Error(
      error.response?.data?.message || "Failed to fetch verification reviews",
    );
  }
};

// Only sends the row details we actually have, so an action from a details page (no row)
// doesn't blank out the review's stored name / snapshot.
const buildPayload = (row = {}, extra = {}) => {
  if (!row || Object.keys(row).length === 0) return { ...extra };
  const payload = {
    displayName: row.name || row.vendorName || row.vehicleNumber || row.displayName || "",
    displayCode: row.uniqueDriverId || row.uniqueVendorCode || row.vehicleNumber || row.displayCode || "",
    mobileNumber: row.mobileNumber || row.phoneNumber || "",
    title: row.title || row.name || row.vendorName || row.vehicleNumber || "",
    snapshot: row,
  };
  Object.keys(payload).forEach((key) => {
    if (payload[key] === "" || payload[key] === "—") delete payload[key];
  });
  return { ...payload, ...extra };
};

const updateVerificationDecision = async ({
  entityType,
  entityId,
  reviewId,
  decision,
  reason = "",
  row = {},
}) => {
  try {
    const url = reviewId
      ? `${SERVER}/admin/verification-reviews/${reviewId}/decision`
      : `${SERVER}/admin/verification-reviews/${entityType}/${entityId}/decision`;

    const response = await axios.patch(
      url,
      buildPayload(row, { decision, reason }),
      {
        headers: authHeaders(),
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error updating verification decision:",
      error.response?.data?.message || error.message,
    );
    throw new Error(
      error.response?.data?.message || "Failed to update verification decision",
    );
  }
};

const updateVerificationProfileDecision = async ({ appUserId, decision, reason = "", row = {} }) => {
  if (!appUserId) throw new Error("appUserId is required for profile decision");
  try {
    const url = `${SERVER}/admin/verification/profiles/${appUserId}/decision`;

    const response = await axios.patch(
      url,
      buildPayload(row, { decision, reason }),
      {
        headers: authHeaders(),
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error updating verification profile decision:",
      error.response?.data?.message || error.message,
    );
    throw new Error(
      error.response?.data?.message || "Failed to update verification profile decision",
    );
  }
};



const requestVerificationClarification = async ({
  entityType,
  entityId,
  reviewId,
  message,
  requestedFields = [],
  row = {},
}) => {
  try {
    const url = reviewId
      ? `${SERVER}/admin/verification-reviews/${reviewId}/clarification`
      : `${SERVER}/admin/verification-reviews/${entityType}/${entityId}/clarification`;

    const response = await axios.post(
      url,
      buildPayload(row, { message, requestedFields }),
      {
        headers: authHeaders(),
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error requesting verification clarification:",
      error.response?.data?.message || error.message,
    );
    throw new Error(
      error.response?.data?.message || "Failed to request clarification",
    );
  }
};

const sendVerificationClarificationReminder = async ({ reviewId }) => {
  try {
    const response = await axios.post(
      `${SERVER}/admin/verification-reviews/${reviewId}/reminder`,
      {},
      {
        headers: authHeaders(),
        withCredentials: true,
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error sending verification clarification reminder:",
      error.response?.data?.message || error.message,
    );
    throw new Error(
      error.response?.data?.message || "Failed to send clarification reminder",
    );
  }
};

const getRequest = async (path, fallbackMessage) => {
  try {
    const response = await axios.get(`${SERVER}${path}`, {
      headers: authHeaders(),
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(fallbackMessage, error.response?.data?.message || error.message);
    throw new Error(error.response?.data?.message || fallbackMessage);
  }
};

// Fields an admin can ask to correct, per entity type: { vendor: [...], driver: [...], vehicle: [...] }
const getFieldCatalog = () =>
  getRequest("/admin/verification-reviews/field-catalog", "Failed to fetch verification fields");

// Review for one entity with current field values, latest clarification round and account summary
const getEntityReview = (entityType, entityId) =>
  getRequest(
    `/admin/verification-reviews/entity/${entityType}/${entityId}`,
    "Failed to fetch verification review",
  );

const getAccountSummary = (appUserId) =>
  getRequest(`/admin/verification/accounts/${appUserId}/summary`, "Failed to fetch account summary");

export {
  getFieldCatalog,
  getEntityReview,
  getAccountSummary,
  getVerificationReviews,
  updateVerificationDecision,
  requestVerificationClarification,
  sendVerificationClarificationReminder,
  updateVerificationProfileDecision,
};
